import { useAuthState } from '@/app/providers/auth'
import { useLocale } from '@/components/Link'
import {
  buildEditOrderLoginHref,
  clearEditOrderParams,
} from '@/components/page-specific/catering/common/helpers/basket-link'
import { useNotificationStore } from '@/store/notificationStore'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  orderId: string
  orderLoadFailed: boolean
}

/**
 * Resolves an edit-order link visit at the page-shell level, like useCateringTeamOrderEntry: a logged-out
 * visitor is sent to login (hard navigation, before the streamed chunk can rewrite the URL), and a
 * signed-in user whose order will not load is told so and dropped back to the plain menu.
 */
export const useExistingOrderEntry = ({ orderId, orderLoadFailed }: Props) => {
  const authState = useAuthState()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const { t } = useTranslation()
  const addNotification = useNotificationStore((state) => state.addNotification)
  const redirectedRef = useRef(false)

  useEffect(() => {
    if (!orderId) return
    if (authState.type === 'unauthenticated' && !redirectedRef.current) {
      redirectedRef.current = true
      window.location.replace(
        buildEditOrderLoginHref({
          pathname: pathname ?? '',
          searchParams,
          locale: locale ?? undefined,
        }),
      )
      return
    }
    if (authState.type === 'authenticated' && orderLoadFailed) {
      addNotification({ severity: 'error', message: t('marketplace:orderNotFound') })
      clearEditOrderParams()
    }
  }, [orderId, orderLoadFailed, authState.type, pathname, searchParams, locale, addNotification, t])
}
